import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Switch,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { graphSettingsRouteConfig } from 'src/app/graph/routes';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

const validationSchema = Yup.object().shape({
  proposalsMustBeReApprovedOnChange: Yup.boolean().required(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const ReApprovalRequirementsModal = ({
  isOpen,
  onClose,
  proposalsMustBeReApprovedOnChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  proposalsMustBeReApprovedOnChange: boolean;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const { graphId } = useRouteParams(graphSettingsRouteConfig);

  const [setProposalsMustBeReApprovedOnChange] = useMutation<
    GraphQLTypes.SetProposalsMustBeReApprovedOnChange,
    GraphQLTypes.SetProposalsMustBeReApprovedOnChangeVariables
  >(gql`
    mutation SetProposalsMustBeReApprovedOnChange(
      $graphId: ID!
      $approvalRequiredOnChange: Boolean!
    ) {
      service(id: $graphId) {
        setProposalsMustBeReApprovedOnChange(
          approvalRequiredOnChange: $approvalRequiredOnChange
        ) {
          ... on PermissionError {
            message
          }
          ... on Service {
            id
            proposalsMustBeReApprovedOnChange
          }
          ... on ValidationError {
            message
          }
        }
      }
    }
  `);

  const {
    values,
    isSubmitting,
    setSubmitting,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { proposalsMustBeReApprovedOnChange },
    onSubmit: async ({
      proposalsMustBeReApprovedOnChange: inputProposalsMustBeReApprovedOnChange,
    }) => {
      if (
        inputProposalsMustBeReApprovedOnChange ===
        proposalsMustBeReApprovedOnChange
      ) {
        onClose();
      }

      setSubmitting(true);
      const { data: responseData } = await setProposalsMustBeReApprovedOnChange(
        {
          variables: {
            graphId,
            approvalRequiredOnChange: inputProposalsMustBeReApprovedOnChange,
          },
        },
      );
      if (
        responseData?.service?.setProposalsMustBeReApprovedOnChange
          ?.__typename === 'Service'
      ) {
        onClose();
        resetForm({});
        setSubmitting(false);
        return;
      }
      setSubmitting(false);

      if (
        responseData?.service?.setProposalsMustBeReApprovedOnChange
          ?.__typename === 'PermissionError' ||
        responseData?.service?.setProposalsMustBeReApprovedOnChange
          ?.__typename === 'ValidationError' ||
        !responseData?.service?.setProposalsMustBeReApprovedOnChange
      ) {
        setErrorMessage(
          responseData?.service?.setProposalsMustBeReApprovedOnChange
            ?.message ?? 'An error has occurred',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            responseData.service.setProposalsMustBeReApprovedOnChange,
            'An error has occurred',
          ),
        );
      }
    },
    validationSchema,
  });

  const handleClose = useCallback(() => {
    onClose();
    resetForm({});
  }, [onClose, resetForm]);

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            Rerequest reviews for new revisions of proposals on this graph
          </ModalTitle>
          <ModalDescription>
            If a proposal is revised after being approved, enabling this setting
            withdraws previous approvals and rerequests reviewers automatically.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <FormControl className="flex flex-row items-center justify-between">
            <FormLabel>Withdraw previous approvals on new revisions</FormLabel>
            <Switch
              isChecked={values.proposalsMustBeReApprovedOnChange}
              onChange={() =>
                setFieldValue(
                  'proposalsMustBeReApprovedOnChange',
                  !values.proposalsMustBeReApprovedOnChange,
                )
              }
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={isSubmitting}
              loadingText="Save"
              onClick={() => handleSubmit()}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
